import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Snackbar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function BankGLMappings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [bankGLAccounts, setBankGLAccounts] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({});
  const [pendingSubsidiaryChanges, setPendingSubsidiaryChanges] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Load all data in parallel
      const [mappingsRes, glAccountsRes, subsidiariesRes, bankGLAccountsRes] = await Promise.all([
        axios.get(`${API_URL}/api/accounting/bank-gl-mappings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/accounting/bank-gl-mappings/gl-accounts?type=bank`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/accounting/bank-gl-mappings/subsidiaries`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/accounting/bank-gl-mappings/gl-accounts/bank-accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (mappingsRes.data?.success) {
        setMappings(mappingsRes.data.data.mappings || []);
      }

      if (glAccountsRes.data?.success) {
        setGlAccounts(glAccountsRes.data.data.accounts || []);
      }

      if (subsidiariesRes.data?.success) {
        setSubsidiaries(subsidiariesRes.data.data.subsidiaries || []);
      }

      if (bankGLAccountsRes.data?.success) {
        setBankGLAccounts(bankGLAccountsRes.data.data.accounts || []);
      }

    } catch (err) {
      console.error('Error loading bank GL mappings:', err);
      setError('Failed to load bank GL mappings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGLChange = (bankAccountId, newGLAccountId) => {
    setPendingChanges(prev => ({
      ...prev,
      [bankAccountId]: newGLAccountId
    }));
  };

  const handleSubsidiaryChange = (glAccountId, newSubsidiaryId) => {
    setPendingSubsidiaryChanges(prev => ({
      ...prev,
      [glAccountId]: newSubsidiaryId
    }));
  };

  const handleSaveAll = async () => {
    const changes = Object.entries(pendingChanges);
    if (changes.length === 0) {
      setError('No changes to save');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Save each change
      for (const [bankAccountId, glAccountId] of changes) {
        await axios.put(
          `${API_URL}/api/accounting/bank-gl-mappings/${encodeURIComponent(bankAccountId)}`,
          { gl_account_id: glAccountId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setSuccess(`Successfully updated ${changes.length} mapping(s)`);
      setPendingChanges({});

      // Reload data to show updated mappings
      await loadData();

    } catch (err) {
      console.error('Error saving mappings:', err);
      setError('Failed to save mappings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSubsidiaries = async () => {
    const changes = Object.entries(pendingSubsidiaryChanges);
    if (changes.length === 0) {
      setError('No subsidiary changes to save');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Save each subsidiary change
      for (const [glAccountId, subsidiaryId] of changes) {
        await axios.put(
          `${API_URL}/api/accounting/bank-gl-mappings/gl-accounts/${glAccountId}/subsidiary`,
          { subsidiary_id: subsidiaryId || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setSuccess(`Successfully updated ${changes.length} GL account subsidiary assignment(s)`);
      setPendingSubsidiaryChanges({});

      // Reload data to show updated subsidiaries
      await loadData();

    } catch (err) {
      console.error('Error saving subsidiary assignments:', err);
      setError('Failed to save subsidiary assignments. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddGLAccount = async () => {
    if (!newAccountName.trim()) {
      setError('Please enter an account name');
      return;
    }

    try {
      setAddingAccount(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accounting/bank-gl-mappings/gl-accounts`,
        { account_name: newAccountName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success) {
        setSuccess(`Created GL account: ${response.data.data.account_code} - ${response.data.data.account_name}`);
        setAddDialogOpen(false);
        setNewAccountName('');
        // Reload data to include the new account
        await loadData();
      }

    } catch (err) {
      console.error('Error creating GL account:', err);
      setError('Failed to create GL account. Please try again.');
    } finally {
      setAddingAccount(false);
    }
  };

  const getCurrentGLValue = (mapping) => {
    if (pendingChanges[mapping.bank_account_id] !== undefined) {
      return pendingChanges[mapping.bank_account_id];
    }
    return mapping.gl_account_id || '';
  };

  const getCurrentSubsidiaryValue = (account) => {
    if (pendingSubsidiaryChanges[account.id] !== undefined) {
      return pendingSubsidiaryChanges[account.id];
    }
    return account.subsidiary_id || '';
  };

  const hasChange = (bankAccountId) => {
    return pendingChanges[bankAccountId] !== undefined;
  };

  const hasSubsidiaryChange = (glAccountId) => {
    return pendingSubsidiaryChanges[glAccountId] !== undefined;
  };

  const formatVolume = (value) => {
    // null = the backend found transactions in a currency with no FX rate
    if (value === null) return 'No FX rate';
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCount = (value) => {
    if (!value) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Filter mappings based on search
  const filteredMappings = mappings.filter(m => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (m.bank_account_name || '').toLowerCase().includes(search) ||
      (m.bank_currency || '').toLowerCase().includes(search) ||
      (m.gl_account_name || '').toLowerCase().includes(search) ||
      (m.entity_name || '').toLowerCase().includes(search)
    );
  });

  // Filter GL accounts based on search
  const filteredBankGLAccounts = bankGLAccounts.filter(a => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (a.account_code || '').toLowerCase().includes(search) ||
      (a.account_name || '').toLowerCase().includes(search) ||
      (a.subsidiary_name || '').toLowerCase().includes(search)
    );
  });

  // Summary stats
  const unmappedCount = mappings.filter(m => !m.has_mapping).length;
  const totalVolume = mappings.reduce((sum, m) => sum + (m.total_volume || 0), 0);
  const unratedCount = mappings.filter(m => m.total_volume === null).length;
  const changesCount = Object.keys(pendingChanges).length;
  const subsidiaryChangesCount = Object.keys(pendingSubsidiaryChanges).length;
  const unassignedSubsidiaryCount = bankGLAccounts.filter(a => !a.subsidiary_id).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Bank Account & GL Mappings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Map external bank accounts to GL accounts and assign subsidiaries
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {(changesCount > 0 || subsidiaryChangesCount > 0) && (
            <Chip
              label={`${changesCount + subsidiaryChangesCount} unsaved change(s)`}
              color="warning"
              size="small"
            />
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => { setError(null); setAddDialogOpen(true); }}
          >
            Add GL Account
          </Button>
          {activeTab === 0 && changesCount > 0 && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Mappings'}
            </Button>
          )}
          {activeTab === 1 && subsidiaryChangesCount > 0 && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSubsidiaries}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Subsidiaries'}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Bank Mappings" />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" />
              GL Account Subsidiaries
              {unassignedSubsidiaryCount > 0 && (
                <Chip label={unassignedSubsidiaryCount} size="small" color="warning" />
              )}
            </Box>
          }
        />
      </Tabs>

      {/* Tab 0: Bank Mappings */}
      {activeTab === 0 && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">Total Accounts</Typography>
              <Typography variant="h5">{mappings.length}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, bgcolor: unmappedCount > 0 ? 'warning.light' : 'success.light' }}>
              <Typography variant="body2" color="text.secondary">Unmapped</Typography>
              <Typography variant="h5">{unmappedCount}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">Total Volume</Typography>
              <Typography variant="h5">{formatVolume(totalVolume)}</Typography>
              {unratedCount > 0 && (
                <Typography variant="caption" color="warning.main">
                  Excludes {unratedCount} account{unratedCount === 1 ? '' : 's'} with no FX rate
                </Typography>
              )}
            </Paper>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by account name, currency, or GL account..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Mappings Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Bank Account</strong></TableCell>
                  <TableCell><strong>Currency</strong></TableCell>
                  <TableCell align="right"><strong>Transactions</strong></TableCell>
                  <TableCell align="right"><strong>Volume</strong></TableCell>
                  <TableCell><strong>GL Account</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMappings.map((mapping) => (
                  <TableRow
                    key={mapping.bank_account_id}
                    sx={{
                      bgcolor: hasChange(mapping.bank_account_id) ? 'action.selected' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {mapping.bank_account_name || '(unnamed)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {mapping.entity_name ? `${mapping.entity_name}` : mapping.bank_source}
                        {mapping.entity_country && ` (${mapping.entity_country})`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={mapping.bank_currency}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCount(mapping.transaction_count)}
                    </TableCell>
                    <TableCell align="right">
                      {formatVolume(mapping.total_volume)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 300 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={getCurrentGLValue(mapping)}
                          onChange={(e) => handleGLChange(mapping.bank_account_id, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>-- Select GL Account --</em>
                          </MenuItem>
                          {glAccounts.map((gl) => (
                            <MenuItem key={gl.id} value={gl.id}>
                              {gl.account_code} - {gl.account_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      {hasChange(mapping.bank_account_id) ? (
                        <Chip
                          label="Modified"
                          color="warning"
                          size="small"
                        />
                      ) : mapping.has_mapping ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredMappings.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {searchTerm ? 'No matching accounts found' : 'No bank accounts found'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Tab 1: GL Account Subsidiaries */}
      {activeTab === 1 && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">Bank GL Accounts</Typography>
              <Typography variant="h5">{bankGLAccounts.length}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1, bgcolor: unassignedSubsidiaryCount > 0 ? 'warning.light' : 'success.light' }}>
              <Typography variant="body2" color="text.secondary">Unassigned Subsidiary</Typography>
              <Typography variant="h5">{unassignedSubsidiaryCount}</Typography>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">Subsidiaries</Typography>
              <Typography variant="h5">{subsidiaries.length}</Typography>
            </Paper>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Assign each GL bank account to its owning subsidiary. This determines which legal entity owns the bank account for intercompany transfer detection.
          </Alert>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by GL account code, name, or subsidiary..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* GL Accounts Subsidiaries Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>GL Code</strong></TableCell>
                  <TableCell><strong>GL Account Name</strong></TableCell>
                  <TableCell><strong>Currency</strong></TableCell>
                  <TableCell sx={{ minWidth: 280 }}><strong>Subsidiary (Legal Entity)</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBankGLAccounts.map((account) => (
                  <TableRow
                    key={account.id}
                    sx={{
                      bgcolor: hasSubsidiaryChange(account.id) ? 'action.selected' : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                        {account.account_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {account.account_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={account.base_currency || 'USD'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={getCurrentSubsidiaryValue(account)}
                          onChange={(e) => handleSubsidiaryChange(account.id, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>-- Select Subsidiary --</em>
                          </MenuItem>
                          {subsidiaries.map((sub) => (
                            <MenuItem key={sub.id} value={sub.id}>
                              {sub.name} ({sub.country})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      {hasSubsidiaryChange(account.id) ? (
                        <Chip
                          label="Modified"
                          color="warning"
                          size="small"
                        />
                      ) : account.subsidiary_id ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <WarningIcon color="warning" fontSize="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredBankGLAccounts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {searchTerm ? 'No matching GL accounts found' : 'No bank GL accounts found'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        message={success}
      />

      {/* Add GL Account Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New GL Account</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new bank/cash GL account for mapping to external bank accounts.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Account Name"
            placeholder="e.g., Wise - Company Name Ltd"
            value={newAccountName}
            onChange={(e) => setNewAccountName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddGLAccount()}
            disabled={addingAccount}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Account code will be auto-assigned (next available 10xx code)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addingAccount}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGLAccount}
            variant="contained"
            disabled={!newAccountName.trim() || addingAccount}
          >
            {addingAccount ? 'Creating...' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
